import { Component, effect, inject, untracked } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';
import { Auth0Store } from '../../../auth0/store';

import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CardComponent } from '../../../../shared-lazy/components/card/card.component';
import { DialogFooterComponent } from '../../../../shared-lazy/components/dialog-footer/dialog-footer.component';
import { DialogHeaderComponent } from '../../../../shared-lazy/components/dialog-header/dialog-header.component';
import { SnakeCaseTitlePipe } from '../../../../shared-lazy/pipes/snakeCaseTitle.pipe';
import { DotsLoaderComponent } from '../../../../shared/components/dots-loader/dots-loader.component';
import { InputDirective } from '../../../../shared/directives/input.directive';
import { LabelDirective } from '../../../../shared/directives/label.directive';
import { ApplicativeRole } from '../../../auth0/models';
import { AuthApiService } from '../../../auth0/services/auth-api.service';

export interface RoleDialogData {
  mode: 'create' | 'edit' | 'view';
  role?: ApplicativeRole;
  isAdmin: boolean;
  isDefaultRole?: boolean;
}

@Component({
  selector: 'gnx-role-dialog',
  templateUrl: './role-dialog.component.html',
  styleUrls: ['./role-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatRadioModule,
    MatTooltip,
    MatProgressSpinner,
    DialogHeaderComponent,
    DialogFooterComponent,
    DotsLoaderComponent,
    CardComponent,
    InputDirective,
    LabelDirective,
  ],
})
export class RoleDialogComponent {
  private dialogRef = inject<MatDialogRef<RoleDialogComponent>>(MatDialogRef);
  data = inject<RoleDialogData>(MAT_DIALOG_DATA);
  private authApiService = inject(AuthApiService);

  protected auth0Store = inject(Auth0Store);

  readonly permissionOptions = [
    { label: 'Allowed', value: true },
    { label: 'Blocked', value: false },
  ];

  readonly dialogTitle: string;

  roleNameControl = new FormControl('', [Validators.required], [this.roleNameValidator.bind(this)]);
  permissionStates: Record<string, boolean> = {};
  private initialPermissionStates: Record<string, boolean> = {};

  private initialSuccessCount = this.auth0Store.saveRoleSuccessCount();

  constructor() {
    this.dialogTitle =
      this.data.mode === 'create'
        ? 'Create Role'
        : this.data.role?.role
          ? new SnakeCaseTitlePipe().transform(this.data.role.role)
          : 'Role';

    effect(() => {
      const sections = this.auth0Store.availablePermissionsGrouped();
      if (!sections) return;
      for (const section of sections) {
        for (const perm of section.permissions) {
          if (!(perm.name in this.permissionStates)) {
            this.permissionStates[perm.name] = false;
          }
        }
      }
      if (this.data.mode === 'edit') {
        this.initialPermissionStates = { ...this.permissionStates };
      }
    });

    effect(() => {
      const count = this.auth0Store.saveRoleSuccessCount();
      if (count > this.initialSuccessCount) {
        untracked(() => this.dialogRef.close(true));
      }
    });

    if (this.data.role) {
      this.roleNameControl.setValue(this.data.role.role);
      this.roleNameControl.disable();
      for (const action of this.data.role.allowed_actions) {
        this.permissionStates[action] = true;
      }
      for (const action of this.data.role.blocked_actions) {
        this.permissionStates[action] = false;
      }
    }
  }

  get isViewMode(): boolean {
    return this.data.mode === 'view';
  }

  get isCreateMode(): boolean {
    return this.data.mode === 'create';
  }

  get canSave(): boolean {
    if (this.isViewMode) {
      return false;
    }
    if (this.isCreateMode) {
      return this.roleNameControl.valid && !this.roleNameControl.pending;
    }
    return this.hasPermissionChanges;
  }

  private get hasPermissionChanges(): boolean {
    return Object.keys(this.permissionStates).some(
      (key) => this.permissionStates[key] !== this.initialPermissionStates[key],
    );
  }

  togglePermission(permissionName: string, allowed: boolean): void {
    if (this.isViewMode) {
      return;
    }
    this.permissionStates[permissionName] = allowed;
  }

  save(): void {
    const allowed: string[] = [];
    const blocked: string[] = [];

    for (const [name, isAllowed] of Object.entries(this.permissionStates)) {
      if (isAllowed) {
        allowed.push(name);
      } else {
        blocked.push(name);
      }
    }

    if (this.data.isDefaultRole) {
      this.auth0Store.saveDefaultRole({
        payload: {
          allowed_permissions: allowed,
          blocked_permissions: blocked,
        },
      });
    } else {
      this.auth0Store.saveApplicativeRole({
        payload: {
          role_name: this.isCreateMode ? this.roleNameControl.value.trim() : this.data.role.role,
          allowed_permissions: allowed,
          blocked_permissions: blocked,
        },
      });
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private roleNameValidator(ctrl: AbstractControl): Observable<ValidationErrors | null> {
    return timer(500).pipe(
      switchMap(() =>
        this.authApiService.checkRoleNameAvailability(ctrl.value.trim()).pipe(
          map((response) => (response.available ? null : { nameExists: true })),
          catchError(() => of(null)),
        ),
      ),
    );
  }
}
